import { SysConst as SC } from '@/trials/plugins/const';
import round from '@/trials/lib/round';

/**
 * 学習データのカラム情報を保持するクラス\
 * 1カラムにつき1つインスタンスが生成される想定です。
 */
export default class TrainColumn {
    /**
     * カラム名
     */
    public name: string;

    /**
     * 有効データ数\
     * ラベルごとの数値を保持
     */
    public validCount: { [key: string]: any } = {};

    /**
     * 欠損値の数\
     * ラベルごとの数値を保持
     */
    public invalidCount: { [key: string]: any } = {};

    /**
     * 型に応じたデータを保持\
     * - 数値 : ラベルごとにデータを保持
     * - 文字列 : 文字列 * ラベル のマトリクスで各出現数を保持
     */
    public data: { [key: string]: any } = {};

    /**
     * このカラムがラベルかどうか
     */
    public isLabel: boolean;

    /**
     * ラベルごとのカウント\
     * カラム同様に、有効データの数・欠損値の数・欠損値の割合・データ数の合計を保持
     */
    public countsForLabel: { [key: string]: any } = {};

    /**
     * データ型\
     * number or string のみの想定ですが、拡張できるよう文字列として保持
     */
    public type: string = '';

    /**
     * ラベルとして使用された値を保持
     */
    public labels: any[] = [];

    /**
     * カテゴリとして使用された値を保持
     * カテゴリ : 文字列データの際の値
     */
    public categories: any[] = [];

    /**
     * コンストラクタ
     * @param name カラム名
     * @param isLabel ラベルかどうか
     */
    constructor(name: string, isLabel: boolean = false) {
        this.name = name;
        this.isLabel = isLabel;
    }

    /**
     * 新しく出現したラベル用のデータの追加
     * @param label ラベル
     */
    private _addLabel(label: any) {
        if (!this.labels.includes(label)) {
            this.labels.push(label);
            this.validCount[label] = 0;
            this.invalidCount[label] = 0;
            this.countsForLabel[label] = {
                val: label,
                validCount: 0,
                invalidCount: 0,
                totalCount: 0,
                invalidRatio: 0,
            };
        }
    }

    /**
     * 新しく出現したカテゴリを追加
     * @param category カテゴリ
     */
    private _addCategories(category: string) {
        if (!this.categories.includes(category)) {
            this.categories.push(category);
        }
    }

    /**
     * カラムにデータを追加します
     * @param val 値
     * @param label ラベル
     */
    public addData(val: any, label: any) {
        // ラベル追加
        this._addLabel(label);

        // カウントアップ
        this.countsForLabel[label].totalCount++;

        // 欠損チェック(欠損の定義)
        if (val == null) {
            this.invalidCount[label]++;
            this.countsForLabel[label].invalidCount++;

            // 欠損値の場合は、ここで処理終了
            return;
        }

        // 型チェック
        if (this.type == '') {
            const type = isNaN(val) ? SC.InputData.Type.STRING : SC.InputData.Type.NUMBER;
            this.type = type;
        }

        this.validCount[label]++;
        this.countsForLabel[label].validCount++;

        // データ型に応じたデータ追加処理
        if (this.type == SC.InputData.Type.NUMBER) {
            if (!(label in this.data)) {
                this.data[label] = [];
            }
            this.data[label].push(val);
        } else {
            if (!(val in this.data)) {
                this.data[val] = {};
                this._addCategories(val);
            }
            if (!(label in this.data[val])) {
                this.data[val][label] = 0;
            }
            this.data[val][label] += 1;
        }
    }

    /**
     * 全データ取得後に行いたい処理を実施します
     */
    public processAfterAllData(): void {
        // 欠損割合計算
        this._calcInvalidRatio();

        // カテゴリリストのソート
        this.categories.sort();

        // ラベルのソート
        this.labels.sort();
    }

    /**
     * 欠損値の割合を計算します
     */
    private _calcInvalidRatio(): void {
        for (let i = 0; i < this.labels.length; i++) {
            const label = this.labels[i];

            const ratio =
                (this.countsForLabel[label].invalidCount / this.countsForLabel[label].totalCount) *
                100;
            this.countsForLabel[label].invalidRatio = round(ratio, 2);
        }
    }
}
